import morphism, { StrictSchema } from 'morphism'
import { Action, Dispatch, MiddlewareAPI } from 'redux'
import { postRequest, urlBuilder } from 'tfs.front-end-utils/requests'

import ClientPassengerInterface from '../../interfaces/client/Passenger'
import ServerPassengerInterface from '../../interfaces/server/Passenger'

import passengerSchema from '../../schemas/client/passenger'

import staticData from '../../config/staticData'

import { addPassengersReferences } from '../../actions/bookings'
import { CREATE_BOOKING } from '../../actions/bookings/createBooking'
import { addNotification } from '../../actions/notifications'
import { addPassengers, passengersFetched } from '../../actions/passengers'

import getLabels from '../../utils/getLabels'
import {
  buildServerNotification,
  dispatchErrorNotifications,
  dispatchSuccessNotification,
} from '../../utils/notifications'
import buildGenericNotificationFromErrors from '../../utils/notifications/buildGenericNotificationFromErrors'
import {
  buildServerNewPassengers,
  getPassengersRefs,
} from '../../utils/passengers'
import getAuthenticationHeaders from '../../utils/requests/generateAuthorizationHeaders'
import { customTourOperatorParameters } from '../../utils/tourOperators'

export interface CreateBookingAction extends Action {
  flightIds: string[]
  passengers: ClientPassengerInterface[]
}

export default (store: MiddlewareAPI) => (next: Dispatch) => async (
  action: CreateBookingAction
) => {
  next(action)

  if (action.type !== CREATE_BOOKING) {
    return
  }

  const {
    endpoints: {
      allotments: { url, headers },
    },
  } = staticData

  const dispatch = store.dispatch

  try {
    const fixedUrl = url.endsWith('/') ? url : `${url}/`
    const requestUrl = urlBuilder(
      `${fixedUrl}bookings`,
      customTourOperatorParameters()
    )

    const requestData = {
      flightIds: action.flightIds,
      passengers: buildServerNewPassengers(action.passengers),
    }

    const authHeaders = await getAuthenticationHeaders(headers)

    const bookingsResponse = await postRequest(requestUrl, {
      headers: { ...headers, ...authHeaders },
      body: JSON.stringify(requestData),
    })

    if (bookingsResponse.errors.length) {
      dispatchErrorNotifications(dispatch, [
        ...bookingsResponse.errors,
        buildGenericNotificationFromErrors(bookingsResponse.errors),
      ])
    } else {
      dispatchSuccessNotification(dispatch, getLabels('passengers_created'))
    }

    if (bookingsResponse.passengers.length) {
      const formattedPassengers: ClientPassengerInterface[] = bookingsResponse.passengers.map(
        (passenger: ServerPassengerInterface) =>
          morphism<
            StrictSchema<ClientPassengerInterface, ServerPassengerInterface>
          >(passengerSchema, passenger)
      )

      dispatch(addPassengers(formattedPassengers))

      const passengersRefs = getPassengersRefs(
        action.passengers,
        formattedPassengers
      )

      if (Object.keys(passengersRefs).length) {
        store.dispatch(addPassengersReferences(passengersRefs))
      }
    }

    dispatch(passengersFetched(action.passengers))
  } catch (error) {
    dispatch(addNotification(buildServerNotification(error)))

    dispatch(passengersFetched(action.passengers))
  }
}
